import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import { getConfig } from '@/config';
import { type DpPriceReviewItem, type DpPriceRow } from '@/infrastructure/CompetitorV2Client';
import { StaffDpPriceApiError, StaffDpPriceClient } from '@/infrastructure/StaffDpPriceClient';
import {
  DP_PRICE_ERRORS,
  SOURCE_HOSTS,
  dpBaselineText,
  dpCompetitorText,
  dpCostText,
  dpCurrentPriceText,
  dpOptionText,
  dpProposedPrice,
  holdReasonLabel,
  safeProductUrl,
  stockStatusLabel,
} from './DpPriceReviewText';

const money = (value: string | null, currency: string) => (value == null ? 'Unavailable' : `${value} ${currency}`);

export function DpStaffPriceReview({
  row,
  onDone,
  onStale,
}: {
  row: DpPriceReviewItem;
  onDone: (result: DpPriceRow) => void;
  /** Called when the row changed after it was opened, so the caller can show the refetched row. */
  onStale: () => void;
}) {
  const { internalApiUrl, apiKey } = getConfig();
  const client = useMemo(() => new StaffDpPriceClient(internalApiUrl, apiKey), [internalApiUrl, apiKey]);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const publish = dpProposedPrice(row);
  // A HELD row has no calculated price, so there is nothing to approve yet (how to handle it is still open).
  const approveBlocked = publish
    ? null
    : 'No new price was calculated, so there is nothing to approve. Reject it or wait for the next price run.';
  const ourUrl = safeProductUrl(row.productUrl, 'skin-seoul.com');
  const competitorUrl = safeProductUrl(row.competitorUrl, SOURCE_HOSTS[row.competitorSource ?? '']);
  const { target } = row;
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [entered, setEntered] = useState('');
  // The server checks cost, the match and the row version; the Admin only checks the amount's shape (POL-4).
  const enteredPrice = entered.trim();
  const enteredValid = /^\d{1,13}(\.\d{1,2})?$/.test(enteredPrice) && Number(enteredPrice) > 0;
  const submit = async (decision: 'APPROVED' | 'REJECTED', salePrice?: string) => {
    if (pending) {
      return;
    }
    setPending(true);
    setMessage('');
    setError('');
    try {
      // Without an amount the server publishes the proposed price into the slot (sale or regular) the product uses;
      // an entered amount is the customer-payable price and replaces the proposal (POL-5, POL-6).
      const result = await client.act(row.targetSku, {
        decision,
        ...(salePrice ? { salePrice } : {}),
        expectedUpdatedAt: row.updatedAt,
      });
      onDone(result);
      setMessage(
        result.status === 'PUBLISHED'
          ? `Published to the store: customers pay ${money(result.proposedSalePrice ?? result.proposedRegularPrice, 'SGD')}.`
          : result.status === 'REJECTED'
            ? 'Proposal rejected. The current store price is unchanged.'
            : `Saved with status ${result.status}.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['dp-price-reviews'] }).catch(() => undefined);
    } catch (caught) {
      if (!(caught instanceof StaffDpPriceApiError)) {
        setError('Result unknown — reload the queue to check the status.');
        return;
      }
      const { status, code } = caught;
      if (code === 'DP_PRICE_REVIEW_STALE') {
        onStale();
      }
      setError(
        code && DP_PRICE_ERRORS[code]
          ? DP_PRICE_ERRORS[code]
          : status === 401
            ? 'The Admin session could not be verified. Reload the page.'
            : status === 403
              ? 'The server did not authorize this price decision.'
              : `The price action failed${code ? `: ${code}` : '.'} The store price was not changed.`,
      );
    } finally {
      setPending(false);
    }
  };
  return (
    <Card>
      <Card.Header>Price review detail · {row.targetSku}</Card.Header>
      <Card.Body>
        <Table responsive bordered size="sm" aria-label="Price review summary" className="align-middle mb-3">
          <tbody>
            {target && (
              <>
                <tr>
                  <th scope="row">Product</th>
                  <td>{target.parentName ?? target.name}</td>
                </tr>
                <tr>
                  <th scope="row">Option</th>
                  <td>{dpOptionText(target)}</td>
                </tr>
                <tr>
                  <th scope="row">SKU</th>
                  <td>{target.parentSku ? `${row.targetSku} (parent ${target.parentSku})` : row.targetSku}</td>
                </tr>
                <tr>
                  <th scope="row">Stock</th>
                  <td>{stockStatusLabel(target.stockStatus)}</td>
                </tr>
              </>
            )}
            <tr>
              <th scope="row">Previous price</th>
              <td>{dpCurrentPriceText(row)}</td>
            </tr>
            <tr>
              <th scope="row">New price</th>
              <td>
                {publish ? `${publish} SGD` : 'No new price'}
                {!row.proposedSalePrice && Number(row.proposedRegularPrice) > Number(row.previousEffectivePrice) && (
                  <div className="small text-muted">
                    Regular price rises to {money(row.proposedRegularPrice, 'SGD')}; the sale price is removed
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">Competitor</th>
              <td>{dpCompetitorText(row)}</td>
            </tr>
            {row.pricingContext && (
              <>
                <tr>
                  <th scope="row">Cost</th>
                  <td>{dpCostText(row.pricingContext)}</td>
                </tr>
                <tr>
                  <th scope="row">Cost_baseline</th>
                  <td>{dpBaselineText(row.pricingContext)}</td>
                </tr>
              </>
            )}
            <tr>
              <th scope="row">Why review</th>
              <td>{row.holdReason ? holdReasonLabel(row.holdReason) : 'None'}</td>
            </tr>
          </tbody>
        </Table>
        <div className="d-flex flex-wrap gap-2">
          <Button disabled={pending || approveBlocked !== null} onClick={() => void submit('APPROVED')}>
            Approve
          </Button>
          <Button variant="outline-danger" disabled={pending} onClick={() => void submit('REJECTED')}>
            Reject
          </Button>
          <Form.Group controlId={`dp-entered-price-${row.targetSku}`} className="d-flex gap-2 align-items-center">
            <Form.Label className="mb-0 text-nowrap">Correct price (SGD)</Form.Label>
            <Form.Control
              className="w-auto"
              inputMode="decimal"
              maxLength={16}
              value={entered}
              onChange={(event) => setEntered(event.target.value)}
            />
            <Button
              variant="outline-primary"
              className="text-nowrap"
              disabled={pending || !enteredValid}
              onClick={() => void submit('APPROVED', enteredPrice)}
            >
              Publish entered price
            </Button>
          </Form.Group>
          {ourUrl && (
            <a className="btn btn-outline-secondary" href={ourUrl} target="_blank" rel="noopener noreferrer">
              Our product
            </a>
          )}
          {competitorUrl && (
            <a className="btn btn-outline-secondary" href={competitorUrl} target="_blank" rel="noopener noreferrer">
              Competitor product
            </a>
          )}
        </div>
        {ourUrl && target?.parentSku && (
          <Form.Text className="d-block">
            Opens the parent page (SKU {target.parentSku}). Select {dpOptionText(target)} to see this option.
          </Form.Text>
        )}
        {target?.stockStatus === 'outofstock' && (
          <Form.Text className="d-block">
            This option is out of stock, so the store page may not show it and Approve will fail.
          </Form.Text>
        )}
        {approveBlocked && <Form.Text className="d-block">{approveBlocked}</Form.Text>}
        {error && (
          <Alert variant="danger" role="alert" className="mt-3">
            {error}
          </Alert>
        )}
        {message && (
          <Alert variant="success" role="status" className="mt-3">
            {message}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
